import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, FileDown, Trash2, Upload, FileText, Inbox } from "lucide-react";
import { toast } from "sonner";
import SEO from "../components/SEO";
import { AdminNav } from "../components/AdminNav";
import { api, API } from "../lib/api";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

const PRODUCT_SLUGS = [
  { slug: "coconut-shell-shisha-charcoal", name: "Shisha Charcoal" },
  { slug: "bbq-coconut-charcoal", name: "BBQ Charcoal" },
];

const DOC_TYPES = [
  { value: "coa", label: "Certificate of Analysis (COA)" },
  { value: "msds", label: "SDS / MSDS" },
  { value: "specification", label: "Specification Sheet" },
  { value: "packaging", label: "Packaging Sheet" },
  { value: "other", label: "Other Document" },
];

const inputClass =
  "h-11 rounded-none border-white/15 bg-charcoal text-offwhite text-sm focus-visible:ring-clay focus-visible:ring-offset-0";

function DocumentRow({ doc, onToggle, onDelete }) {
  return (
    <div
      data-testid={`document-row-${doc.id}`}
      className="bg-charcoal px-5 sm:px-7 py-5 flex flex-wrap items-center gap-x-5 gap-y-3"
    >
      <FileText className="w-5 h-5 text-clay shrink-0" />
      <div className="min-w-0">
        <p className="text-sm font-semibold text-offwhite truncate">{doc.label}</p>
        <p className="text-xs text-offwhite/45 mt-0.5">
          {PRODUCT_SLUGS.find((p) => p.slug === doc.product_slug)?.name || doc.product_slug} ·{" "}
          {DOC_TYPES.find((t) => t.value === doc.doc_type)?.label || doc.doc_type} ·{" "}
          {(doc.size / 1024).toFixed(0)} KB
        </p>
      </div>
      <span
        className={`text-[10px] font-bold uppercase tracking-[0.2em] border px-2.5 py-1 ml-auto ${
          doc.is_public ? "bg-forest/20 text-emerald-300 border-forest" : "bg-white/5 text-offwhite/50 border-white/20"
        }`}
      >
        {doc.is_public ? "Public" : "Private"}
      </span>
      <div className="flex items-center gap-2">
        <a
          href={`${API}/api/documents/${doc.id}/download`}
          data-testid={`document-download-${doc.id}`}
          className="w-9 h-9 flex items-center justify-center border border-white/20 text-offwhite/60 hover:border-offwhite hover:text-offwhite transition-colors duration-300"
          title="Download"
        >
          <FileDown className="w-4 h-4" />
        </a>
        <button
          data-testid={`document-toggle-${doc.id}`}
          onClick={() => onToggle(doc)}
          className="h-9 px-3 text-xs font-semibold border border-white/20 text-offwhite/60 hover:border-offwhite hover:text-offwhite transition-colors duration-300"
        >
          {doc.is_public ? "Make Private" : "Make Public"}
        </button>
        <button
          data-testid={`document-delete-${doc.id}`}
          onClick={() => onDelete(doc)}
          className="w-9 h-9 flex items-center justify-center border border-red-900/50 text-red-400/70 hover:border-red-400 hover:text-red-400 transition-colors duration-300"
          title="Delete"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

export default function AdminDocuments() {
  const [docs, setDocs] = useState(null);
  const [productSlug, setProductSlug] = useState(PRODUCT_SLUGS[0].slug);
  const [docType, setDocType] = useState("coa");
  const [label, setLabel] = useState("");
  const [isPublic, setIsPublic] = useState(true);
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const navigate = useNavigate();

  const load = useCallback(() => {
    api
      .get("/api/documents")
      .then((res) => setDocs(res.data.documents))
      .catch(() => navigate("/admin/login"));
  }, [navigate]);

  useEffect(() => {
    load();
  }, [load]);

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!file || !label.trim()) {
      toast.error("Please choose a file and enter a label");
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("product_slug", productSlug);
      formData.append("doc_type", docType);
      formData.append("label", label.trim());
      formData.append("is_public", isPublic);
      await api.post("/api/documents", formData);
      toast.success("Document uploaded");
      setLabel("");
      setFile(null);
      if (fileRef.current) fileRef.current.value = "";
      load();
    } catch (err) {
      toast.error(typeof err.response?.data?.detail === "string" ? err.response.data.detail : "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const togglePublic = async (doc) => {
    try {
      await api.patch(`/api/documents/${doc.id}`, { is_public: !doc.is_public });
      setDocs((prev) => prev.map((d) => (d.id === doc.id ? { ...d, is_public: !d.is_public } : d)));
      toast.success(doc.is_public ? "Document set to private" : "Document published");
    } catch {
      toast.error("Failed to update document");
    }
  };

  const remove = async (doc) => {
    if (!window.confirm(`Delete "${doc.label}"?`)) return;
    try {
      await api.delete(`/api/documents/${doc.id}`);
      setDocs((prev) => prev.filter((d) => d.id !== doc.id));
      toast.success("Document deleted");
    } catch {
      toast.error("Failed to delete document");
    }
  };

  let listContent;
  if (docs === null) {
    listContent = (
      <div className="flex items-center gap-3 text-offwhite/50">
        <Loader2 className="w-5 h-5 animate-spin" /> Loading documents…
      </div>
    );
  } else if (docs.length === 0) {
    listContent = (
      <div
        data-testid="documents-empty"
        className="border border-white/10 bg-white/[0.03] p-14 flex flex-col items-center gap-4 text-center"
      >
        <Inbox className="w-8 h-8 text-offwhite/30" />
        <p className="text-offwhite/50 text-sm">
          No documents yet. Upload verified COA, MSDS or specification sheets to publish them on product pages.
        </p>
      </div>
    );
  } else {
    listContent = (
      <div data-testid="documents-list" className="space-y-px bg-white/10 border border-white/10">
        {docs.map((doc) => (
          <DocumentRow key={doc.id} doc={doc} onToggle={togglePublic} onDelete={remove} />
        ))}
      </div>
    );
  }

  return (
    <>
      <SEO title="Documents | HAQILA Admin" description="HAQILA product document management." />
      <div data-testid="admin-documents-page" className="bg-charcoal min-h-screen pt-32 pb-24">
        <div className="max-w-[1100px] mx-auto px-5 sm:px-10">
          <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.25em] text-clay mb-2">HAQILA Admin</p>
              <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-offwhite">
                Product Documents
              </h1>
            </div>
            <AdminNav active="Documents" />
          </div>

          <form
            data-testid="document-upload-form"
            onSubmit={handleUpload}
            className="border border-white/10 bg-white/[0.03] p-6 sm:p-8 grid sm:grid-cols-2 gap-5 mb-10"
          >
            <div className="space-y-2">
              <Label className="text-xs font-bold uppercase tracking-[0.18em] text-offwhite/50">Product</Label>
              <Select value={productSlug} onValueChange={setProductSlug}>
                <SelectTrigger data-testid="doc-product-select" className={inputClass}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PRODUCT_SLUGS.map((p) => (
                    <SelectItem key={p.slug} value={p.slug}>
                      {p.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-bold uppercase tracking-[0.18em] text-offwhite/50">Document Type</Label>
              <Select value={docType} onValueChange={setDocType}>
                <SelectTrigger data-testid="doc-type-select" className={inputClass}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {DOC_TYPES.map((t) => (
                    <SelectItem key={t.value} value={t.value}>
                      {t.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-bold uppercase tracking-[0.18em] text-offwhite/50">Label</Label>
              <Input
                data-testid="doc-label-input"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                className={inputClass}
                placeholder="e.g. COA — Cube 25mm, Batch Aug 2026"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-bold uppercase tracking-[0.18em] text-offwhite/50">
                File (PDF, PNG, JPG — max 15MB)
              </Label>
              <Input
                data-testid="doc-file-input"
                ref={fileRef}
                type="file"
                accept=".pdf,.png,.jpg,.jpeg,.webp"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                className={`${inputClass} file:text-offwhite/70 file:text-xs file:mr-3 pt-2.5`}
              />
            </div>
            <label className="flex items-center gap-3 sm:col-span-2 cursor-pointer" data-testid="doc-public-toggle">
              <input
                type="checkbox"
                checked={isPublic}
                onChange={(e) => setIsPublic(e.target.checked)}
                className="w-4 h-4 accent-[#1F4529]"
              />
              <span className="text-sm text-offwhite/70">
                Public — visible and downloadable on the product page. Unchecked documents stay private.
              </span>
            </label>
            <div className="sm:col-span-2">
              <button
                type="submit"
                data-testid="doc-upload-submit"
                disabled={uploading}
                className="inline-flex items-center gap-2 bg-forest hover:bg-moss disabled:opacity-60 text-offwhite font-semibold px-7 py-3.5 text-sm tracking-wide transition-colors duration-300"
              >
                {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                Upload Document
              </button>
            </div>
          </form>

          {listContent}
        </div>
      </div>
    </>
  );
}
